/**
 * Гостевой профиль — настройки уведомлений + привязка Telegram.
 * Tanstack Query поверх @hsc/api-client (customFetch). Server-state только
 * через React Query (патент проекта).
 *
 * Бэк:
 *   GET  /v1/notifications/preferences        → { channels: {cat:[ch]}, ... }
 *   PUT  /v1/notifications/preferences         { enable_channels|disable_channels }
 *   POST /v1/me/telegram/bind-link             → { deep_link, bot_username, ... }
 *
 * Гостевые категории — request_status (статус заявки) + request_chat (новое
 * сообщение в чате). Ползунок переключает канал telegram для категории
 * НАПРЯМУЮ (PATCH-семантика PUT enable/disable), без кнопки «Изменить».
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customFetch } from '@hsc/api-client';

/** Гостевая категория уведомления. */
export type GuestNotifCategory = 'request_status' | 'request_chat';

/** Канал по умолчанию для гостевого ползунка. */
export const GUEST_CHANNEL = 'telegram' as const;

export interface PreferencesResponse {
  user_id: string;
  tenant_id: string;
  /** Категория → включённые каналы. Присутствие telegram = ползунок ON. */
  channels: Record<string, string[]>;
  manager_locked: boolean;
}

interface UpdatePrefsBody {
  enable_channels?: Record<string, string[]>;
  disable_channels?: Record<string, string[]>;
}

const PREFS_KEY = ['notification-preferences'] as const;

async function fetchPreferences(): Promise<PreferencesResponse> {
  return customFetch<PreferencesResponse>({
    url: '/notifications/preferences',
    method: 'GET',
    baseURL: '/v1',
  });
}

async function updatePreferences(body: UpdatePrefsBody): Promise<PreferencesResponse> {
  return customFetch<PreferencesResponse>({
    url: '/notifications/preferences',
    method: 'PUT',
    baseURL: '/v1',
    data: body,
  });
}

/** GET настроек уведомлений текущего пользователя. */
export function useNotificationPreferences() {
  return useQuery<PreferencesResponse>({
    queryKey: PREFS_KEY,
    queryFn: fetchPreferences,
    staleTime: 30_000,
  });
}

/** Прямое переключение канала telegram для гостевой категории (ON/OFF). */
export function useToggleGuestCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ category, on }: { category: GuestNotifCategory; on: boolean }) =>
      updatePreferences(
        on
          ? { enable_channels: { [category]: [GUEST_CHANNEL] } }
          : { disable_channels: { [category]: [GUEST_CHANNEL] } },
      ),
    onSuccess: (data) => {
      qc.setQueryData(PREFS_KEY, data);
    },
  });
}

export interface BindLinkResponse {
  deep_link: string;
  bot_username: string;
  token: string;
  expires_in_seconds: number;
}

/** POST /v1/me/telegram/bind-link — выдаёт deep-link бота для привязки TG. */
export function useTelegramBindLink() {
  return useMutation<BindLinkResponse, unknown, void>({
    mutationFn: () =>
      customFetch<BindLinkResponse>({
        url: '/me/telegram/bind-link',
        method: 'POST',
        baseURL: '/v1',
      }),
  });
}

/** Включён ли канал telegram для категории (ползунок ON). */
export function isCategoryOn(prefs: PreferencesResponse | undefined, category: GuestNotifCategory): boolean {
  if (!prefs) return false;
  const channels = prefs.channels?.[category] ?? [];
  return channels.includes(GUEST_CHANNEL);
}
